/**
 * SkillGlobalSettingsDrawer - Drawer for global skill settings.
 * Contains dedup configuration and future extensible settings.
 */

import React from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { X, Settings2, Info } from 'lucide-react';
import { SkillDedupConfig } from './SkillDedupConfig';
import { useI18n } from '@/i18n';

interface SkillDedupValues {
  enabled?: boolean;
  data_query_hours?: number | null;
  report_window_hours?: number | null;
  max_per_window?: number | null;
  output_preview_length?: number | null;
  show_user_query?: boolean | null;
  query_preview_length?: number | null;
}

interface SkillGlobalSettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Current skill_dedup config values */
  dedupConfig: SkillDedupValues;
  /** Handler for dedup config changes */
  onDedupConfigChange: (field: string, value: boolean | number | null) => void;
  /** Whether skill_tracking is enabled (required for dedup) */
  skillTrackingEnabled?: boolean;
}

/**
 * Drawer for global skill settings.
 * Contains dedup configuration and future extensible settings.
 */
export const SkillGlobalSettingsDrawer: React.FC<SkillGlobalSettingsDrawerProps> = ({
  open,
  onClose,
  dedupConfig,
  onDedupConfigChange,
  skillTrackingEnabled = false,
}) => {
  const { messages } = useI18n();
  const skills = messages.agentConfig.skills;

  if (!open) return null;

  const dedupEnabled = dedupConfig.enabled ?? false;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-25 z-40"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 w-[480px] max-w-full bg-white shadow-xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-purple-500" />
            <h2 className="text-lg font-medium text-gray-900">{skills?.globalSettings || '技能全局设置'}</h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="p-1"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Skill Tracking Warning */}
          {!skillTrackingEnabled && (
            <Card className="p-4 bg-amber-50 border-amber-200">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-800">{skills?.skillTrackingRequired || '需要启用技能追踪'}</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    {skills?.skillTrackingRequiredHint || '技能去重功能需要同时启用「技能追踪」Hook 来记录执行历史。请在 Hooks Pipeline 中启用 skill_tracking。'}
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Dedup Configuration Section */}
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="font-medium text-gray-900">{skills?.dedupConfig || '去重配置'}</h3>
            </div>

            {/* Enable Dedup Toggle */}
            <div className="mb-4 pb-4 border-b border-gray-200">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={dedupEnabled}
                  onChange={(e) => onDedupConfigChange('enabled', e.target.checked)}
                  className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-700">{skills?.enableDedup || '启用技能去重'}</span>
                  <p className="text-xs text-gray-500 mt-1">
                    {skills?.enableDedupDesc || '为激活的技能注入历史执行记录，让模型了解之前输出过什么内容，避免生成重复内容。'}
                  </p>
                </div>
              </label>
            </div>

            {/* Dedup Config Fields - only when enabled */}
            {dedupEnabled && (
              <SkillDedupConfig
                values={{
                  data_query_hours: dedupConfig.data_query_hours,
                  report_window_hours: dedupConfig.report_window_hours,
                  max_per_window: dedupConfig.max_per_window,
                  output_preview_length: dedupConfig.output_preview_length,
                  show_user_query: dedupConfig.show_user_query,
                  query_preview_length: dedupConfig.query_preview_length,
                }}
                onChange={onDedupConfigChange}
                disabled={!dedupEnabled}
              />
            )}

            {!dedupEnabled && (
              <p className="text-sm text-gray-500 italic">
                {skills?.dedupConfigHint || '启用去重后，可配置历史记录的查询范围和显示方式。'}
              </p>
            )}
          </Card>

          {/* How It Works */}
          <Card className="p-4 bg-blue-50 border-blue-200">
            <h4 className="font-medium text-blue-800 mb-2">{skills?.dedupPrinciple || '去重原理'}</h4>
            <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
              {(skills?.dedupPrincipleList || [
                '当技能被匹配时，系统会查询该用户该技能的历史执行记录',
                '历史记录会以 <skill_history> 标签注入到技能指令中',
                '模型可以参考历史输出，生成差异化的新内容',
                '每个技能可以单独覆盖这些全局默认值',
              ]).map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </>
  );
};

export default SkillGlobalSettingsDrawer;
